import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  UsersIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

import { listParticipants } from '../../api/participantApi';
import { getAssessment }    from '../../api/assessmentApi';
import { reanalyze }        from '../../api/analysisApi';
import { exportReport }     from '../../api/reportApi';
import Badge      from '../../components/ui/Badge';
import Card       from '../../components/ui/Card';
import Spinner    from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';

// ── Helpers ───────────────────────────────────────────────────────────────────
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function scoreColor(score) {
  if (score == null) return 'text-neutral-400';
  if (score >= 80)   return 'text-success-600 font-semibold';
  if (score >= 60)   return 'text-secondary-600 font-semibold';
  if (score >= 40)   return 'text-accent-600 font-semibold';
  return 'text-error-500 font-semibold';
}

function statusVariant(status) {
  const map = {
    submitted: 'success',
    draft:     'warning',
    pending:   'warning',
    analyzed:  'primary',
    started:   'info',
  };
  return map[status] ?? 'default';
}

function statusLabel(status) {
  const map = {
    draft:     'In Progress',
    submitted: 'Submitted',
    analyzed:  'Analyzed',
    pending:   'Pending',
    started:   'Started',
  };
  return map[status] ?? status ?? 'Unknown';
}

function StatCard({ icon: Icon, label, value, colorClass }) {
  return (
    <div className="flex items-center gap-4 rounded-card border border-neutral-200 bg-white px-5 py-4 shadow-card">
      <div
        className={clsx('flex h-10 w-10 items-center justify-center rounded-full', colorClass)}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-heading font-bold text-neutral-900">{value ?? '—'}</p>
        <p className="text-xs text-neutral-500">{label}</p>
      </div>
    </div>
  );
}

const PER_PAGE = 20;

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ParticipantListPage() {
  const { id: assessmentId } = useParams();

  const [assessment, setAssessment]       = useState(null);
  const [participants, setParticipants]   = useState([]);
  const [stats, setStats]                 = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const [searchRaw, setSearchRaw]         = useState('');
  const search                            = useDebounce(searchRaw, 400);
  const [statusFilter, setStatusFilter]   = useState('');
  const [page, setPage]                   = useState(1);
  const [totalPages, setTotalPages]       = useState(1);

  const [reanalyzingAll, setReanalyzingAll] = useState(false);
  const [exporting, setExporting]           = useState(false);

  // Load assessment title
  useEffect(() => {
    if (!assessmentId) return;
    getAssessment(assessmentId)
      .then((res) => setAssessment(res.data.data ?? res.data))
      .catch(() => {});
  }, [assessmentId]);

  const fetchParticipants = useCallback(() => {
    if (!assessmentId) return;
    setLoading(true);
    setError(null);
    const params = { page, per_page: PER_PAGE };
    if (search)       params.search = search;
    if (statusFilter) params.status = statusFilter;

    listParticipants(assessmentId, params)
      .then((res) => {
        const d = res.data;
        const list = d.data ?? [];
        setParticipants(list);
        setTotalPages(d.meta?.last_page ?? d.last_page ?? 1);

        // Use server-provided stats or derive them
        if (d.stats) {
          setStats(d.stats);
        } else {
          setStats({
            total:     d.meta?.total ?? list.length,
            submitted: list.filter((p) => ['submitted', 'analyzed'].includes(p.status)).length,
            pending:   list.filter((p) => ['draft', 'pending', 'started'].includes(p.status)).length,
            analyzed:  list.filter((p) => p.status === 'analyzed').length,
          });
        }
      })
      .catch((err) => setError(err?.response?.data?.message ?? 'Failed to load participants'))
      .finally(() => setLoading(false));
  }, [assessmentId, search, statusFilter, page]);

  useEffect(() => { setPage(1); }, [search, statusFilter]);
  useEffect(() => { fetchParticipants(); }, [fetchParticipants]);

  const handleReanalyzeAll = async () => {
    const submitted = participants.filter(
      (p) => ['submitted', 'analyzed'].includes(p.status) && p.submission_id
    );
    if (submitted.length === 0) {
      toast('No submitted participants to analyze');
      return;
    }
    setReanalyzingAll(true);
    let ok = 0, fail = 0;
    await Promise.all(
      submitted.map((p) =>
        reanalyze(p.submission_id)
          .then(() => { ok++; })
          .catch(() => { fail++; })
      )
    );
    setReanalyzingAll(false);
    if (fail === 0) {
      toast.success(`Re-analysis triggered for ${ok} participant${ok !== 1 ? 's' : ''}`);
    } else {
      toast(`Triggered ${ok}, failed ${fail}`, { icon: '⚠️' });
    }
    fetchParticipants();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res  = await exportReport(assessmentId, 'csv');
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `participants-${assessmentId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={`/assessments/${assessmentId}`}
            className="rounded-button p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">
              Participants
              {assessment?.title && (
                <span className="ml-2 font-normal text-neutral-400">
                  — {assessment.title}
                </span>
              )}
            </h1>
            <p className="mt-0.5 text-sm text-neutral-500">
              Manage and review assessment submissions
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleReanalyzeAll}
            disabled={reanalyzingAll}
            className="inline-flex items-center gap-2 rounded-button border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-button hover:bg-neutral-50 disabled:opacity-60 transition"
          >
            {reanalyzingAll ? (
              <Spinner size="sm" className="text-neutral-500" />
            ) : (
              <ArrowPathIcon className="h-4 w-4" />
            )}
            Re-analyze All
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {exporting ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            icon={UsersIcon}
            label="Total"
            value={stats.total}
            colorClass="bg-primary-100 text-primary-600"
          />
          <StatCard
            icon={CheckCircleIcon}
            label="Submitted"
            value={stats.submitted}
            colorClass="bg-success-100 text-success-600"
          />
          <StatCard
            icon={ClockIcon}
            label="Pending"
            value={stats.pending}
            colorClass="bg-warning-100 text-warning-600"
          />
          <StatCard
            icon={ChartBarIcon}
            label="Analyzed"
            value={stats.analyzed}
            colorClass="bg-secondary-100 text-secondary-600"
          />
        </div>
      )}

      {/* Search + Filter */}
      <Card className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={searchRaw}
              onChange={(e) => setSearchRaw(e.target.value)}
              placeholder="Search by name, email, or startup..."
              className="w-full rounded-input border border-neutral-200 py-2 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="">All Statuses</option>
            <option value="draft">In Progress</option>
            <option value="submitted">Submitted</option>
            <option value="analyzed">Analyzed</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" className="text-primary-600" />
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <p className="text-sm text-error-500">{error}</p>
            <button
              onClick={fetchParticipants}
              className="mt-2 text-sm text-primary-600 underline"
            >
              Retry
            </button>
          </div>
        ) : participants.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No participants found"
            description="No participants match your current search or filters."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 text-left">
                    {['Rank', 'Participant', 'Startup', 'Email', 'Score', 'Status', 'Submitted At', 'Actions'].map(
                      (col) => (
                        <th
                          key={col}
                          className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400 last:pr-0"
                        >
                          {col}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {participants.map((p, idx) => (
                    <tr key={p.submission_id ?? p.id} className="group hover:bg-neutral-50 transition">
                      <td className="py-3 pr-4 font-mono text-xs text-neutral-400">
                        {p.rank ?? (page - 1) * PER_PAGE + idx + 1}
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium text-neutral-900">
                          {p.full_name ?? p.name ?? '—'}
                        </p>
                      </td>
                      <td className="py-3 pr-4 text-neutral-600">
                        {p.startup_name ?? '—'}
                      </td>
                      <td className="py-3 pr-4 text-neutral-500">{p.email ?? '—'}</td>
                      <td className={clsx('py-3 pr-4', scoreColor(p.overall_score))}>
                        {p.overall_score != null ? p.overall_score : '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={statusVariant(p.status)}>
                          {statusLabel(p.status)}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-xs text-neutral-500">
                        {p.submitted_at
                          ? new Date(p.submitted_at).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="py-3">
                        <Link
                          to={`/participants/${p.participant_id ?? p.id}`}
                          className="inline-flex items-center gap-1.5 rounded-button px-2.5 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 opacity-0 group-hover:opacity-100 transition"
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-4">
                <p className="text-sm text-neutral-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-button p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 transition"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pg = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pg}
                        onClick={() => setPage(pg)}
                        className={clsx(
                          'h-7 w-7 rounded-button text-xs font-medium transition',
                          pg === page
                            ? 'bg-primary-600 text-white'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        )}
                      >
                        {pg}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="rounded-button p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 transition"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
